import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Client } from '../models/client';
import { ImmobilierService } from '../services/immobilier.service';
import { AddEditClientDialogComponent } from './add-edit-client-dialog/add-edit-client-dialog.component';
import { ClientDetailDialogComponent } from './client-detail-dialog/client-detail-dialog.component';
import { ExcelService } from '../excel.service';
import { PdfService } from '../services/pdf.service';


@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.css']
})
export class ClientsComponent implements OnInit {

  displayedColumns: string[] = ['reference', 'name', 'type', 'phone', 'email', 'city', 'status', 'actions'];
  dataSource: MatTableDataSource<Client>;
  clients: Client[] = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Filters
  filterType: string = '';
  filterStatus: string = '';
  searchText: string = '';

  // Pagination
  pageSize = 10;
  currentPage = 0;


  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private immobilierService: ImmobilierService,
    private excelService: ExcelService,
    private pdfService: PdfService
  ) {

    this.dataSource = new MatTableDataSource(this.clients);
  }

  ngOnInit(): void {
    this.loadClients();
  }

  loadClients() {
    this.immobilierService.getClients().subscribe({
      next: (data) => {
        this.clients = data;
        this.dataSource.data = this.clients;
        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
        }
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
      },
      error: (err) => {
        console.error(err);
        this.showNotification('Erreur lors du chargement des clients');
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Custom filter predicate
    this.dataSource.filterPredicate = (data: Client, filter: string) => {
      const searchString = filter.toLowerCase();
      const name = data.personType === 'morale' ? data.company : `${data.firstName} ${data.lastName}`;

      const matchesSearch =
        name.toLowerCase().includes(searchString) ||
        (data.email && data.email.toLowerCase().includes(searchString)) ||
        (data.phone && data.phone.includes(searchString)) ||
        (data.city && data.city.toLowerCase().includes(searchString));

      const matchesType = this.filterType ? data.type === this.filterType : true;
      const matchesStatus = this.filterStatus ? data.status === this.filterStatus : true;

      return matchesSearch && matchesType && matchesStatus;
    };
  }

  applyFilter() {
    this.dataSource.filter = this.searchText.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    // Force re-filter
    this.dataSource.filter = this.searchText.trim().toLowerCase();
  }

  onFilterChange() {
    this.dataSource.filter = this.searchText.trim().toLowerCase() || ' ';
    if (!this.searchText) {
      this.dataSource.filter = ' ';
    }
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }


  openAddDialog() {
    const dialogRef = this.dialog.open(AddEditClientDialogComponent, {
      width: '900px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadClients();
      }
    });
  }

  openEditDialog(client: Client) {
    const dialogRef = this.dialog.open(AddEditClientDialogComponent, {
      width: '900px',
      disableClose: true,
      data: { client: client }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadClients();
      }
    });
  }

  openDetailDialog(client: Client) {
    const dialogRef = this.dialog.open(ClientDetailDialogComponent, {
      width: '800px',
      data: { client: client }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.action === 'edit') {
          this.openEditDialog(result.client);
        } else if (result.action === 'convert') {
          this.convertProspect(result.client);
        }
      }
    });
  }

  convertProspect(client: Client) {
    if (confirm(`Voulez-vous vraiment convertir ${client.firstName} ${client.lastName} en Client ?`)) {
      client.type = 'client';
      client.conversionDate = new Date();
      this.immobilierService.updateClient(client.id, client).subscribe({
        next: () => {
          this.showNotification('Prospect converti en Client avec succès');
          this.loadClients();
        },
        error: (err) => {
          console.error(err);
          this.showNotification('Erreur lors de la conversion');
        }
      });
    }
  }

  showNotification(message: string) {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    });
  }

  exportToExcel(): void {
    const dataToExport = this.dataSource.filteredData.map(c => ({
      'ID': c.id,
      'Nom': c.personType === 'morale' ? c.company : c.firstName + ' ' + c.lastName,
      'Type': c.type,
      'Téléphone': c.phone,
      'Email': c.email,
      'Ville': c.city,
      'Statut': c.status
    }));
    this.excelService.generateExcel(dataToExport, 'Liste_Clients_Prospects');
  }

  exportToPdf(): void {
    this.pdfService.generatePdf('clients-table-container', 'Liste_Clients_Prospects');
  }

  printPage(): void {
    window.print();
  }
}

